import { Search, SlidersHorizontal, Package, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency } from '@/lib/utils';
import { HighlightMatch } from '@/components/internal/line-items/HighlightMatch';
import type { CatalogItem } from '../hooks/useCatalogSearch';

interface CatalogGridProps {
  items: CatalogItem[];
  isLoading: boolean;
  isFetching: boolean;
  totalCount: number;
  totalPages: number;
  page: number;
  onPageChange: (page: number) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isSearchScopedToCategory: boolean;
  onToggleSearchScope: () => void;
  browseCategoryName: string | null;
  onAddItem: (item: CatalogItem, componentType: string) => void;
  isAddingItem: string | null;
  cartItemIds: string[];
  isLockedStatus: boolean;
  onOpenFilters: () => void;
  activeFiltersCount: number;
  classificationMap: Record<string, string>;
}

const TYPE_BADGES: Record<
  CatalogItem['item_type'],
  { label: string; className: string }
> = {
  internal: { label: 'Internal', className: 'bg-blue-500 text-white border-transparent' },
  assembly: { label: 'Assembly', className: 'bg-purple-500 text-white border-transparent' },
  system: { label: 'System', className: 'bg-teal-500 text-white border-transparent' },
};

export function CatalogGrid({
  items,
  isLoading,
  isFetching,
  totalCount,
  totalPages,
  page,
  onPageChange,
  searchQuery,
  onSearchChange,
  isSearchScopedToCategory,
  onToggleSearchScope,
  browseCategoryName,
  onAddItem,
  isAddingItem,
  cartItemIds,
  isLockedStatus,
  onOpenFilters,
  activeFiltersCount,
  classificationMap,
}: CatalogGridProps) {
  return (
    <div className="flex flex-col min-w-0 w-full">
      {/* Top bar */}
      <div className="border-b p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenFilters}
            className="relative shrink-0"
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span className="ml-2">Filters</span>
            {activeFiltersCount > 0 && (
              <Badge
                variant="default"
                className="ml-2 h-5 min-w-5 px-1.5 text-xs"
              >
                {activeFiltersCount}
              </Badge>
            )}
          </Button>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search items..."
              className="pl-9"
            />
          </div>


          {isFetching && !isLoading && (
            <span className="text-xs text-muted-foreground shrink-0">
              Updating...
            </span>
          )}
        </div>

        {searchQuery.length > 0 && browseCategoryName && (
          <div className="text-xs text-muted-foreground pl-1">
            {isSearchScopedToCategory ? (
              <>
                Searching in <span className="font-medium">{browseCategoryName}</span>
                {' · '}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={onToggleSearchScope}
                >
                  Search all categories
                </button>
              </>
            ) : (
              <>
                Searching all categories
                {' · '}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={onToggleSearchScope}
                >
                  Back to {browseCategoryName}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Breadcrumb */}
      <div className="px-3 py-2 border-b text-xs text-muted-foreground flex items-center gap-1">
        {browseCategoryName ? (
          <>
            <span>All</span>
            <ChevronRight className="h-3 w-3" />
            <span className="font-medium text-foreground">{browseCategoryName}</span>
            <span className="mx-1">·</span>
            <span>{totalCount} items</span>
          </>
        ) : (
          <span>All items · {totalCount} items</span>
        )}
      </div>

      {/* Grid area */}
      <div className="p-3 pb-24">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="w-full aspect-[4/3]" />
                <div className="p-3 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-3" />
            <div className="text-base font-medium">No items found</div>
            <div className="text-sm text-muted-foreground mt-1">
              Try adjusting your filters or search term
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((item) => {
              const inCart = cartItemIds.includes(item.id);
              const adding = isAddingItem === `${item.item_type}-${item.id}`;
              const badge = TYPE_BADGES[item.item_type];
              const image = item.image_urls?.[0];

              return (
                <Card key={item.id} className="overflow-hidden flex flex-col">
                  <div className="relative bg-muted flex items-center justify-center overflow-hidden" style={{ height: '160px' }}>
                    {image ? (
                      <img
                        src={image}
                        alt={item.name}
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        style={{ maxHeight: '160px' }}
                      />
                    ) : (
                      <Package className="h-10 w-10 text-muted-foreground" />
                    )}
                    {badge && (
                      <Badge
                        className={cn(
                          'absolute top-2 right-2 text-[10px]',
                          badge.className
                        )}
                      >
                        {badge.label}
                      </Badge>
                    )}
                  </div>

                  <div className="p-2 flex-1 flex flex-col gap-1">
                    <div
                      className="font-semibold text-xs line-clamp-2"
                      title={item.name}
                    >
                      <HighlightMatch text={item.name} query={searchQuery} />
                    </div>
                    <div className="text-xs text-foreground">
                      {formatCurrency(item.final_price)}
                      {item.unit_symbol ? ` / ${item.unit_symbol}` : ''}
                    </div>
                    {item.item_type === 'system' && (
                      <div className="text-[11px] text-muted-foreground">
                        Base price — your markup applies
                      </div>
                    )}
                    {item.category_id && classificationMap[item.category_id] && (
                      <Badge variant="secondary" className="self-start text-[10px]">
                        {classificationMap[item.category_id]}
                      </Badge>
                    )}

                    <div className="mt-auto pt-2">
                      <Button
                        size="sm"
                        variant={inCart ? 'default' : 'outline'}
                        className="w-full h-7 text-xs py-0"
                        disabled={isLockedStatus || adding}
                        onClick={() => onAddItem(item, item.item_type)}
                      >
                        {adding
                          ? 'Adding...'
                          : inCart
                          ? '✓ Added'
                          : 'Add to Estimate'}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t p-3 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default CatalogGrid;
